// src/components/work_orders/CreateWorkOrderForm.js
// Enhanced modal form component for creating new work orders with advanced features and debugging
// --- UPDATE (v2 — any FlowSense work)
//   • "What is this work on?" — domain (water asset, sensor, well, road, facility, fleet,
//     internal), work order TYPE (corrective, emergency, preventive, predictive, inspection,
//     installation, replacement, project, internal) and category (incl. Road Paving, Line
//     Striping, Road Marking, Sensor Calibration, Well Desilting …) from workOrderCatalog
//   • Links the job to its FlowSense record (asset / sensor / well / road) + location
//   • Pre-fill from anywhere: `prefill` prop, navigation state { woDraft } or ?create=1 links
//   • Inspections: create an inspection, link it to a parent work order, or tick
//     "Schedule a QA inspection" to create a linked inspection after this job
//   • AI assistant (WorkOrderCreateGuide): live suggestion, "Ask FlowSense AI", step-by-step
//     checklist of what is needed, and what the AI does after you dispatch
// --- END UPDATE

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody,
    ModalCloseButton, Button, VStack, FormControl, FormLabel, Input, Select,
    Textarea, useColorModeValue, useToast, HStack, Icon, Badge, Box, Text,
    InputGroup, InputRightElement, Tooltip, IconButton, Collapse, Progress,
    Alert, AlertIcon, AlertDescription, Divider, ScaleFade, Tag, TagLabel,
    FormHelperText, NumberInput, NumberInputField, Fade, Grid, GridItem,
    Spinner,
    // --- UPDATE
    Checkbox, SimpleGrid,
    // --- END UPDATE
} from '@chakra-ui/react';
import {
    MdAdd, MdTitle, MdDescription, MdCalendarToday, MdPerson, MdPriorityHigh,
    MdAssignment, MdCheckCircle, MdWarning, MdInfo, MdBugReport, MdClose,
    MdLightbulb, MdAutorenew,
    // --- UPDATE: Added MdTimer for SLA Deadline Preview field ---
    MdTimer,
    // --- END UPDATE ---
    // --- UPDATE
    MdPlace, MdLink, MdCategory, MdFactCheck,
    // --- END UPDATE
} from 'react-icons/md';
import { FaBrain } from 'react-icons/fa';

import { config } from '../../config';
import { getAuthToken, getUserInfo } from '../../utils/auth';
// --- UPDATE
import WorkOrderCreateGuide from './WorkOrderCreateGuide';
import {
    WORK_ORDER_DOMAINS, WORK_ORDER_TYPES, DOMAIN_KEYS, TYPE_KEYS, categoriesFor, typesFor,
    getDomain, getType, findCategory, suggestTitle, validateDraft, validateForCreate, INSPECTION_SOURCES,
} from './workOrderCatalog';
import { createLinkedInspection } from '../../services/workOrderService';
import { publish as publishBus, BUS_EVENTS } from '../../utils/flowsenseBus';
// --- END UPDATE

// ============================================================================
// DEBUG CONFIGURATION
// ============================================================================
const DEBUG_CONFIG = {
    enabled: process.env.NODE_ENV === 'development' || process.env.REACT_APP_DEBUG === 'true',
    logLevel: 'all',
    logToConsole: true,
    logToUI: false,
    logNetwork: true,
    logPerformance: true,
    logValidation: true,
};

const debugLog = {
    info: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
        if (DEBUG_CONFIG.logLevel === 'error' || DEBUG_CONFIG.logLevel === 'warn') return;
        console.log(`%c[CreateWO:${context}] ℹ️ ${message}`, 'color: #3182ce', data || '');
    },
    warn: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
        if (DEBUG_CONFIG.logLevel === 'error') return;
        console.warn(`%c[CreateWO:${context}] ⚠️ ${message}`, 'color: #d69e2e', data || '');
    },
    error: (context, message, error = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logToConsole) return;
        console.error(`%c[CreateWO:${context}] ❌ ${message}`, 'color: #e53e3e', error || '');
        if (error?.stack) console.error(error.stack);
    },
    network: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logNetwork) return;
        console.log(`%c[CreateWO:${context}] 🌐 ${message}`, 'color: #805ad5', data || '');
    },
    performance: (context, message, duration) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logPerformance) return;
        const color = duration < 100 ? '#38a169' : duration < 500 ? '#d69e2e' : '#e53e3e';
        console.log(`%c[CreateWO:${context}] ⚡ ${message} (${duration}ms)`, `color: ${color}`);
    },
    validation: (context, message, data = null) => {
        if (!DEBUG_CONFIG.enabled || !DEBUG_CONFIG.logValidation) return;
        console.log(`%c[CreateWO:${context}] ✅ ${message}`, 'color: #38b2ac', data || '');
    },
};

class PerformanceMonitor {
    constructor(operation) {
        this.operation = operation;
        this.startTime = performance.now();
    }
    end() {
        const duration = (performance.now() - this.startTime).toFixed(2);
        debugLog.performance(this.operation, 'Operation completed', duration);
        return { duration };
    }
}

// ============================================================================
// API CALLS
// ============================================================================
const API_BASE_URL = config.apiUrl;

const createWorkOrderApi = async (workOrderData, token) => {
    const monitor = new PerformanceMonitor('createWorkOrderApi');
    debugLog.network('createWorkOrderApi', 'Creating work order', workOrderData);

    const response = await fetch(`${API_BASE_URL}/workorders`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(workOrderData),
    });

    if (!response.ok) {
        let errorMsg = `HTTP error creating work order! Status: ${response.status}`;
        try {
            const errData = await response.json();
            errorMsg = errData.message || (errData.errors ? errData.errors.map(er => er.msg).join(', ') : errorMsg);
        } catch (e) {
            debugLog.error('createWorkOrderApi', 'Failed to parse error response', e);
        }
        debugLog.error('createWorkOrderApi', errorMsg);
        throw new Error(errorMsg);
    }

    const result = await response.json();
    monitor.end();
    debugLog.info('createWorkOrderApi', 'Work order created successfully', result);
    return result;
};

const fetchOllamaTriageDescription = async (assetContext, token) => {
    debugLog.network('fetchOllamaTriageDescription', 'Requesting AI triage brief', assetContext);

    const response = await fetch(`${API_BASE_URL}/ai/asset-triage`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(assetContext),
    });

    if (!response.ok) {
        let errorMsg = `Ollama triage failed: ${response.status}`;
        try {
            const errData = await response.json();
            errorMsg = errData.message || errorMsg;
        } catch (_) {}
        throw new Error(errorMsg);
    }

    const data = await response.json();
    return data.brief || '';
};

// ============================================================================
// CONSTANTS
// ============================================================================
const ALLOWED_STATUS   = ['pending', 'in_progress', 'completed', 'cancelled'];
const ALLOWED_PRIORITY = ['low', 'medium', 'high', 'critical'];

// --- UPDATE: Added slaTarget (hours) to each priority tier ---
const PRIORITY_CONFIG = {
    low: {
        color: 'gray',
        icon: MdInfo,
        description: 'Low priority - can be scheduled for later',
        slaTarget: 72,
    },
    medium: {
        color: 'blue',
        icon: MdPriorityHigh,
        description: 'Medium priority - normal workflow',
        slaTarget: 24,
    },
    high: {
        color: 'orange',
        icon: MdWarning,
        description: 'High priority - requires prompt attention',
        slaTarget: 8,
    },
    critical: {
        color: 'red',
        icon: MdWarning,
        description: 'Critical - immediate action required',
        slaTarget: 4,
    },
};
// --- END UPDATE ---

// --- UPDATE: catalog fields carried by the form (all optional for the backend)
const CATALOG_FIELDS = [
    'domain', 'work_order_type', 'category', 'asset_id', 'sensor_id', 'well_id', 'road_id',
    'latitude', 'longitude', 'location_description', 'parent_work_order_id', 'inspection_ref_id',
    'inspection_source', 'source', 'issue_id',
];
const DRAFT_FIELDS = [...CATALOG_FIELDS, 'title', 'description', 'priority', 'due_date', 'status', 'assigned_user_id'];
const QA_INSPECTION_DAYS = 3;
const isoDatePlus = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().split('T')[0]; };
// --- END UPDATE

// ============================================================================
// MAIN COMPONENT
// ============================================================================
function CreateWorkOrderForm({ isOpen, onClose, onSuccess, prefill }) {
    const initialFormState = {
        title: '',
        description: '',
        status: 'pending',
        priority: 'medium',
        due_date: '',
        assigned_user_id: '',
        // --- UPDATE
        domain: '', work_order_type: '', category: '', asset_id: '', sensor_id: '', well_id: '', road_id: '',
        latitude: '', longitude: '', location_description: '', parent_work_order_id: '', inspection_ref_id: '',
        inspection_source: '', source: 'manual', issue_id: '',
        // --- END UPDATE
    };

    const location = useLocation();

    const [formData, setFormData]               = useState(initialFormState);
    const [isSubmitting, setIsSubmitting]       = useState(false);
    const [showDebugPanel, setShowDebugPanel]   = useState(DEBUG_CONFIG.logToUI);
    const [validationErrors, setValidationErrors] = useState({});
    const [characterCounts, setCharacterCounts] = useState({ title: 0, description: 0 });
    const [formProgress, setFormProgress]       = useState(0);
    const [showTips, setShowTips]               = useState(false);
    const [isGeneratingDesc, setIsGeneratingDesc] = useState(false);
    const [isAITriageMode, setIsAITriageMode]   = useState(false);

    // --- UPDATE: SLA deadline preview state ---
    const [slaPreview, setSlaPreview] = useState('');
    // --- END UPDATE ---

    // --- UPDATE: catalog / inspection / pre-fill state
    const [scheduleQa, setScheduleQa] = useState(false);
    const [showGps, setShowGps] = useState(false);
    const [prefillOrigin, setPrefillOrigin] = useState(null); // e.g. "Road Bay St"
    const priorityTouchedRef = useRef(false);
    const prefillRef = useRef(prefill);
    prefillRef.current = prefill;
    // --- END UPDATE

    const toast       = useToast();
    const currentUser = getUserInfo();

    // Debug: Component lifecycle
    useEffect(() => {
        debugLog.info('useEffect:mount', 'Component mounted');
        return () => debugLog.info('useEffect:unmount', 'Component unmounted');
    }, []);

    // --- UPDATE: Recalculate SLA preview whenever priority changes ---
    useEffect(() => {
        const targetHours = PRIORITY_CONFIG[formData.priority]?.slaTarget || 24;
        const targetDate  = new Date();
        targetDate.setHours(targetDate.getHours() + targetHours);
        setSlaPreview(targetDate.toLocaleString());
    }, [formData.priority]);
    // --- END UPDATE ---

    // Interceptor — reads navigation state sent by LiveGrid
    useEffect(() => {
        const autoDraftData = location.state;
        if (!autoDraftData || !autoDraftData.autoTriage) return;

        debugLog.info('interceptor', 'Arrived from LiveGrid AI triage flow', autoDraftData);
        setIsAITriageMode(true);

        const autoTitle = `AI Triage: Inspect ${autoDraftData.assetType || 'Asset'} — ${autoDraftData.assetName}`;
        const autoPriority =
            autoDraftData.healthScore != null
                ? autoDraftData.healthScore < 40
                    ? 'critical'
                    : autoDraftData.healthScore < 70
                    ? 'high'
                    : 'medium'
                : 'high';

        setFormData(prev => ({
            ...prev,
            title: autoTitle,
            priority: autoPriority,
            description: '🤖 AI is analyzing sensor data — writing technical brief...',
        }));

        debugLog.info('interceptor', 'Step A complete — form pre-filled', { autoTitle, autoPriority });

        const fetchAIBrief = async () => {
            setIsGeneratingDesc(true);
            const token = getAuthToken();
            if (!token) {
                debugLog.warn('interceptor', 'No auth token — skipping Ollama call');
                setFormData(prev => ({
                    ...prev,
                    description: `Inspect ${autoDraftData.assetName}. Health score: ${autoDraftData.healthScore ?? 'N/A'}%.`,
                }));
                setIsGeneratingDesc(false);
                return;
            }

            try {
                const brief = await fetchOllamaTriageDescription(
                    {
                        asset_id:                autoDraftData.assetId,
                        asset_name:              autoDraftData.assetName,
                        asset_type:              autoDraftData.assetType,
                        health_score:            autoDraftData.healthScore,
                        last_psi:                autoDraftData.currentReading,
                        predicted_failure_date:  autoDraftData.predictedFailureDate,
                        remaining_useful_life:   autoDraftData.remainingUsefulLife,
                        zone_name:               autoDraftData.zoneName,
                        location_description:    autoDraftData.locationDescription,
                    },
                    token
                );

                debugLog.info('interceptor', 'Step B complete — Ollama brief received', { briefLength: brief.length });
                setFormData(prev => ({ ...prev, description: brief }));

                toast({
                    title: 'AI Brief Ready',
                    description: 'Technical description generated by Ollama AI.',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } catch (error) {
                debugLog.error('interceptor', 'Ollama call failed', error);
                setFormData(prev => ({
                    ...prev,
                    description:
                        `Inspect ${autoDraftData.assetName} (${autoDraftData.assetType || 'Asset'}). ` +
                        `Current health score: ${autoDraftData.healthScore ?? 'N/A'}%. ` +
                        (autoDraftData.currentReading != null
                            ? `Last recorded pressure: ${autoDraftData.currentReading} PSI. `
                            : '') +
                        (autoDraftData.remainingUsefulLife != null
                            ? `Estimated remaining useful life: ${autoDraftData.remainingUsefulLife} days. `
                            : '') +
                        `Zone: ${autoDraftData.zoneName || 'N/A'}.`,
                }));
                toast({
                    title: 'AI Brief Unavailable',
                    description: 'Could not reach Ollama — description pre-filled with sensor data.',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
            } finally {
                setIsGeneratingDesc(false);
            }
        };

        fetchAIBrief();
        window.history.replaceState({}, document.title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset form when modal opens
    useEffect(() => {
        if (isOpen) {
            if (isAITriageMode) {
                debugLog.info('useEffect:reset', 'AI triage mode — skipping full reset');
                setValidationErrors({});
                setIsAITriageMode(false);
                return;
            }

            debugLog.info('useEffect:reset', 'Resetting form for new work order');
            setFormData(initialFormState);
            setValidationErrors({});
            setCharacterCounts({ title: 0, description: 0 });
            setFormProgress(0);

            const defaultDueDate = new Date();
            defaultDueDate.setDate(defaultDueDate.getDate() + 7);
            const formattedDate = defaultDueDate.toISOString().split('T')[0];
            setFormData(prev => ({ ...prev, due_date: formattedDate }));
            debugLog.info('useEffect:reset', 'Auto-set due date', { due_date: formattedDate });

            // --- UPDATE: pre-fill from the page that opened the form (asset, sensor, well, road, inspection …)
            priorityTouchedRef.current = false;
            setScheduleQa(false);
            const draft = prefillRef.current;
            if (draft && typeof draft === 'object') {
                const patch = {};
                DRAFT_FIELDS.forEach((k) => {
                    if (draft[k] !== undefined && draft[k] !== null && draft[k] !== '') patch[k] = String(draft[k]);
                });
                const t = getType(patch.work_order_type);
                if (t && !patch.priority) patch.priority = t.defaultPriority;
                const cat = findCategory(patch.category, patch.domain);
                if (cat && !draft.priority) patch.priority = cat.priority;
                if (!patch.title && (patch.category || patch.work_order_type)) patch.title = suggestTitle(patch, draft._entityName);
                if (patch.priority) priorityTouchedRef.current = true;
                setShowGps(!!(patch.latitude || patch.longitude));
                setFormData(prev => ({ ...prev, ...patch }));
                setPrefillOrigin(draft._entityName || patch.location_description || null);
                debugLog.info('useEffect:reset', 'Applied pre-fill draft', patch);
            } else {
                setPrefillOrigin(null);
            }
            // --- END UPDATE
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Form completion progress
    useEffect(() => {
        const requiredFields = ['title'];
        const optionalFields = ['description', 'due_date', 'assigned_user_id', 'domain', 'work_order_type']; // --- UPDATE: + domain/type
        const allFields      = [...requiredFields, ...optionalFields];
        const filledFields   = allFields.filter(f => formData[f] && formData[f].toString().trim() !== '').length;
        const progress       = (filledFields / allFields.length) * 100;
        setFormProgress(Math.round(progress));
        debugLog.validation('formProgress', `Form ${progress}% complete`, { filledFields, totalFields: allFields.length });
    }, [formData]);

    // Character counts
    useEffect(() => {
        setCharacterCounts({
            title:       formData.title.length,
            description: formData.description.length,
        });
    }, [formData.title, formData.description]);

    const validateField = useCallback((name, value) => {
        const errors = {};
        switch (name) {
            case 'title':
                if (!value || value.trim().length === 0) {
                    errors.title = 'Title is required';
                } else if (value.length < 3) {
                    errors.title = 'Title must be at least 3 characters';
                } else if (value.length > 200) {
                    errors.title = 'Title must not exceed 200 characters';
                }
                break;
            case 'description':
                if (value && value.length > 2000) {
                    errors.description = 'Description must not exceed 2000 characters';
                }
                break;
            case 'due_date':
                if (value) {
                    const selectedDate = new Date(value);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    if (selectedDate < today) {
                        errors.due_date = 'Due date cannot be in the past';
                    }
                }
                break;
            default:
                break;
        }
        if (Object.keys(errors).length > 0) {
            debugLog.validation('validateField', `Validation errors for ${name}`, errors);
        }
        return errors;
    }, []);

    // --- UPDATE: keep domain → type → category → priority consistent
    const applyCascade = useCallback((prev, patch) => {
        const next = { ...prev, ...patch };
        if ('domain' in patch) {
            if (next.category && !categoriesFor(next.domain).some(c => c.label === next.category)) next.category = '';
            if (next.work_order_type && next.domain && !typesFor(next.domain).includes(next.work_order_type)) next.work_order_type = '';
        }
        if ('category' in patch && next.category) {
            const cat = findCategory(next.category, next.domain);
            if (cat) {
                if (!next.domain) next.domain = cat.domain;
                if (!('work_order_type' in patch) && !prev.work_order_type) next.work_order_type = cat.type;
                if (!('priority' in patch) && !priorityTouchedRef.current) next.priority = cat.priority;
            }
        }
        if ('work_order_type' in patch && next.work_order_type && !('priority' in patch) && !priorityTouchedRef.current) {
            const t = getType(next.work_order_type);
            if (t) next.priority = t.defaultPriority;
        }
        if (next.work_order_type === 'emergency' && !['high', 'critical'].includes(next.priority)) next.priority = 'critical';
        return next;
    }, []);

    /** Used by the AI assistant's Apply buttons. */
    const handleApplySuggestion = useCallback((patch) => {
        if ('priority' in patch) priorityTouchedRef.current = true;
        setFormData(prev => applyCascade(prev, patch));
        toast({ title: 'Suggestion applied', status: 'info', duration: 1500 });
    }, [applyCascade, toast]);
    // --- END UPDATE

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        debugLog.info('handleInputChange', `Field "${name}" changed`, { oldValue: formData[name], newValue: value });
        // --- UPDATE: catalog cascade for domain / type / category; track manual priority
        if (name === 'priority') priorityTouchedRef.current = true;
        if (['domain', 'work_order_type', 'category'].includes(name)) {
            setFormData(prev => applyCascade(prev, { [name]: value }));
            return;
        }
        // --- END UPDATE
        setFormData(prev => ({ ...prev, [name]: value }));
        const fieldErrors = validateField(name, value);
        setValidationErrors(prev => ({
            ...prev,
            ...fieldErrors,
            [name]: fieldErrors[name] || undefined,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const monitor = new PerformanceMonitor('handleSubmit');
        debugLog.info('handleSubmit', 'Form submission initiated', formData);

        const titleErrors = validateField('title', formData.title);
        if (Object.keys(titleErrors).length > 0) {
            debugLog.warn('handleSubmit', 'Validation failed', titleErrors);
            setValidationErrors(titleErrors);
            toast({ title: "Validation Error", description: titleErrors.title, status: "error", duration: 5000, isClosable: true });
            return;
        }

        // --- UPDATE: same hard rules the backend enforces (sensor id, emergency priority …)
        const hardErrors = validateForCreate(formData);
        if (hardErrors.length > 0) {
            toast({ title: 'Missing information', description: hardErrors.join(' '), status: 'error', duration: 6000, isClosable: true });
            return;
        }
        // --- END UPDATE

        setIsSubmitting(true);
        const token = getAuthToken();

        if (!token) {
            debugLog.error('handleSubmit', 'Auth token missing');
            toast({ title: "Authentication Error", description: "Authentication token missing", status: "error", duration: 5000, isClosable: true });
            setIsSubmitting(false);
            return;
        }

        if (!currentUser?.id) {
            debugLog.error('handleSubmit', 'Current user ID missing');
            toast({ title: "Error", description: "Could not identify creating user.", status: "error", duration: 5000, isClosable: true });
            setIsSubmitting(false);
            return;
        }

        const payload = {
            ...formData,
            created_by_id:    currentUser.id,
            description:      formData.description || null,
            due_date:         formData.due_date || null,
            assigned_user_id: formData.assigned_user_id || null,
        };
        // --- UPDATE: send catalog / link fields only when filled (keeps the payload valid for older APIs)
        CATALOG_FIELDS.forEach((k) => {
            const v = formData[k];
            if (v === undefined || v === null || String(v).trim() === '') delete payload[k];
            else payload[k] = typeof v === 'string' ? v.trim() : v;
        });
        if (payload.latitude !== undefined) payload.latitude = Number(payload.latitude);
        if (payload.longitude !== undefined) payload.longitude = Number(payload.longitude);
        // --- END UPDATE

        debugLog.network('handleSubmit', 'Submitting new Work Order', payload);

        try {
            const result = await createWorkOrderApi(payload, token);
            debugLog.info('handleSubmit', 'Work order created successfully', result);

            // --- UPDATE: Toast confirms SLA clocks have started ---
            toast({
                title: "Work Order Created",
                description: `Successfully dispatched. SLA Clocks have started.`,
                status: "success",
                duration: 4000,
                isClosable: true,
            });
            // --- END UPDATE ---

            // --- UPDATE: optional linked QA inspection + tell the other FlowSense pages
            const created = result?.workOrder || result?.data?.workOrder || result?.data || result; // --- UPDATE: { success, data } responses
            if (scheduleQa && created?.id) {
                try {
                    await createLinkedInspection(created.id, { due_date: formData.due_date || isoDatePlus(QA_INSPECTION_DAYS) });
                    toast({ title: 'QA inspection scheduled', description: 'A linked inspection work order was created for this job.', status: 'success', duration: 4000, isClosable: true });
                } catch (qaErr) {
                    toast({ title: 'Inspection not created', description: `${qaErr.message} — you can add it later from the work order.`, status: 'warning', duration: 6000, isClosable: true });
                }
            }
            publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, {
                action: 'created', id: created?.id ?? null, asset_id: payload.asset_id ?? null, road_id: payload.road_id ?? null,
                sensor_id: payload.sensor_id ?? null, well_id: payload.well_id ?? null, domain: payload.domain ?? null, work_order_type: payload.work_order_type ?? null,
            });
            // --- END UPDATE

            monitor.end();
            onSuccess();
            onClose();
        } catch (err) {
            debugLog.error('handleSubmit', 'Failed to create work order', err);
            toast({ title: "Creation Failed", description: err.message, status: "error", duration: 7000, isClosable: true });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAutoFill = useCallback(() => {
        const sampleData = {
            title:            `Sample Work Order - ${new Date().toLocaleTimeString()}`,
            description:      'This is a sample work order created for testing purposes. It includes a detailed description of the work to be performed.',
            status:           'pending',
            priority:         'medium',
            due_date:         new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            assigned_user_id: '',
        };
        setFormData(sampleData);
        debugLog.info('handleAutoFill', 'Auto-filled with sample data', sampleData);
        toast({ title: "Auto-filled", description: "Form filled with sample data", status: "info", duration: 3000 });
    }, [toast]);

    // ============================================================================
    // THEME COLORS — all hoisted unconditionally
    // ============================================================================
    const formLabelColor       = useColorModeValue("gray.700",   "gray.200");
    const modalBg              = useColorModeValue("white",      "gray.800");
    const borderColor          = useColorModeValue("gray.200",   "gray.600");
    const mutedTextColor       = useColorModeValue("gray.500",   "gray.400");
    const cardBg               = useColorModeValue("gray.50",    "gray.700");
    const debugPanelBg         = useColorModeValue("yellow.50",  "yellow.900");
    const debugBorderColor     = useColorModeValue("yellow.200", "yellow.700");
    const progressBg           = useColorModeValue("gray.100",   "gray.700");
    const aiTriageBannerBg     = useColorModeValue("purple.50",  "purple.900");
    const aiTriageBannerBorder = useColorModeValue("purple.200", "purple.600");
    // --- UPDATE: SLA preview box colors ---
    const slaBg                = useColorModeValue("orange.50",  "orange.900");
    // --- END UPDATE ---

    const getPriorityColor = () => PRIORITY_CONFIG[formData.priority]?.color || 'gray';

    // --- UPDATE: catalog-derived view state
    const sectionBg = useColorModeValue('teal.50', 'rgba(49, 151, 149, 0.12)');
    const sectionBorder = useColorModeValue('teal.200', 'teal.700');
    const domainCfg = getDomain(formData.domain);
    const typeCfg = getType(formData.work_order_type);
    const categoryOptions = useMemo(() => categoriesFor(formData.domain), [formData.domain]);
    const typeOptions = useMemo(() => (formData.domain ? typesFor(formData.domain) : TYPE_KEYS), [formData.domain]);
    const selectedCategory = findCategory(formData.category, formData.domain);
    const readiness = useMemo(() => validateDraft(formData), [formData]);
    const isInspection = formData.work_order_type === 'inspection';
    // --- END UPDATE

    // ============================================================================
    // RENDER
    // ============================================================================
    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside" size="xl">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ScaleFade in={isOpen} initialScale={0.95}>
                <ModalContent as="form" onSubmit={handleSubmit} bg={modalBg} borderRadius="xl" boxShadow="2xl">

                    {/* ==================== HEADER ==================== */}
                    <ModalHeader>
                        <VStack align="stretch" spacing={2}>
                            <HStack spacing={3}>
                                <Icon as={MdAdd} boxSize={6} color="teal.500" />
                                <Text fontSize="lg" fontWeight="bold">{isInspection ? 'Create Inspection' : 'Create New Work Order'}</Text>{/* --- UPDATE */}
                                {isGeneratingDesc && (
                                    <HStack
                                        px={2} py={1}
                                        bg={aiTriageBannerBg}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={aiTriageBannerBorder}
                                        spacing={1.5}
                                    >
                                        <Spinner size="xs" color="purple.500" />
                                        <Icon as={FaBrain} color="purple.500" boxSize={3} />
                                        <Text fontSize="xs" fontWeight="700" color="purple.600">AI Writing...</Text>
                                    </HStack>
                                )}
                            </HStack>

                            {/* Progress Bar */}
                            <Box>
                                <HStack justify="space-between" mb={1}>
                                    <Text fontSize="xs" color={mutedTextColor}>Form Completion</Text>
                                    <Badge colorScheme={formProgress === 100 ? 'green' : 'blue'}>{formProgress}%</Badge>
                                </HStack>
                                <Progress
                                    value={formProgress}
                                    size="xs"
                                    colorScheme={formProgress === 100 ? 'green' : 'teal'}
                                    borderRadius="full"
                                    bg={progressBg}
                                />
                            </Box>
                        </VStack>
                    </ModalHeader>

                    <ModalCloseButton />

                    {/* ==================== BODY ==================== */}
                    <ModalBody pb={6}>
                        <VStack spacing={4} align="stretch">

                            {/* AI Triage origin banner */}
                            {isGeneratingDesc && (
                                <Alert
                                    status="info"
                                    borderRadius="md"
                                    variant="left-accent"
                                    colorScheme="purple"
                                    bg={aiTriageBannerBg}
                                    borderColor={aiTriageBannerBorder}
                                >
                                    <AlertIcon as={FaBrain} color="purple.500" />
                                    <Box fontSize="sm">
                                        <Text fontWeight="bold" color="purple.700">
                                            Ollama AI is generating the technical description
                                        </Text>
                                        <Text fontSize="xs" color="purple.600">
                                            Fields have been pre-filled from the Live Grid asset context.
                                            The description will update automatically when the AI brief is ready.
                                        </Text>
                                    </Box>
                                </Alert>
                            )}

                            {/* Quick Actions */}
                            <HStack justify="space-between">
                                <Tooltip label="Show helpful tips">
                                    <Button
                                        size="xs"
                                        variant="ghost"
                                        leftIcon={<MdLightbulb />}
                                        onClick={() => setShowTips(!showTips)}
                                        colorScheme={showTips ? 'yellow' : 'gray'}
                                    >
                                        {showTips ? 'Hide' : 'Show'} Tips
                                    </Button>
                                </Tooltip>
                                <HStack spacing={2}>
                                    {DEBUG_CONFIG.enabled && (
                                        <>
                                            <Tooltip label="Auto-fill with sample data">
                                                <IconButton
                                                    icon={<MdAutorenew />}
                                                    size="xs"
                                                    variant="outline"
                                                    colorScheme="purple"
                                                    onClick={handleAutoFill}
                                                    aria-label="Auto-fill"
                                                />
                                            </Tooltip>
                                            <Tooltip label="Toggle debug panel">
                                                <IconButton
                                                    icon={<MdBugReport />}
                                                    size="xs"
                                                    variant={showDebugPanel ? 'solid' : 'outline'}
                                                    colorScheme="yellow"
                                                    onClick={() => setShowDebugPanel(!showDebugPanel)}
                                                    aria-label="Debug"
                                                />
                                            </Tooltip>
                                        </>
                                    )}
                                </HStack>
                            </HStack>

                            {/* Tips Panel */}
                            <Collapse in={showTips} animateOpacity>
                                <Alert status="info" borderRadius="md" variant="left-accent">
                                    <AlertIcon />
                                    <Box fontSize="sm">
                                        <Text fontWeight="bold" mb={1}>Quick Tips:</Text>
                                        <VStack align="start" spacing={1} fontSize="xs">
                                            <Text>• Use clear, descriptive titles</Text>
                                            <Text>• Set realistic due dates</Text>
                                            <Text>• Provide detailed descriptions for better clarity</Text>
                                            <Text>• Assign critical priority only when necessary</Text>
                                        </VStack>
                                    </Box>
                                </Alert>
                            </Collapse>

                            {/* --- UPDATE: What is this work on? ------------------------------------ */}
                            <Box bg={sectionBg} borderWidth="1px" borderColor={sectionBorder} borderRadius="lg" p={3} role="group" aria-label="What is this work on">
                                <HStack mb={2} justify="space-between" flexWrap="wrap">
                                    <HStack>
                                        <Icon as={MdCategory} color="teal.500" />
                                        <Text fontSize="sm" fontWeight="bold">What is this work on?</Text>
                                    </HStack>
                                    {prefillOrigin && <Tag size="sm" colorScheme="teal"><TagLabel>From: {prefillOrigin}</TagLabel></Tag>}
                                </HStack>
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                                    <FormControl>
                                        <FormLabel fontSize="xs" color={formLabelColor} mb={1}>Domain</FormLabel>
                                        <Select name="domain" size="sm" value={formData.domain} onChange={handleInputChange} placeholder="Choose what the work is on" aria-label="Domain" focusBorderColor="teal.500">
                                            {DOMAIN_KEYS.map(k => <option key={k} value={k}>{WORK_ORDER_DOMAINS[k].label}</option>)}
                                        </Select>
                                        {domainCfg && <FormHelperText fontSize="2xs">{domainCfg.explain}</FormHelperText>}
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="xs" color={formLabelColor} mb={1}>Work order type</FormLabel>
                                        <Select name="work_order_type" size="sm" value={formData.work_order_type} onChange={handleInputChange} placeholder="Choose the type of work" aria-label="Work order type" focusBorderColor="teal.500">
                                            {typeOptions.map(k => <option key={k} value={k}>{WORK_ORDER_TYPES[k].label}</option>)}
                                        </Select>
                                        {typeCfg && <FormHelperText fontSize="2xs">{typeCfg.explain}</FormHelperText>}
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel fontSize="xs" color={formLabelColor} mb={1}>Category</FormLabel>
                                        <Select name="category" size="sm" value={formData.category} onChange={handleInputChange} placeholder={formData.domain ? 'Choose the job' : 'Choose a domain first'} isDisabled={!formData.domain} aria-label="Category" focusBorderColor="teal.500">
                                            {categoryOptions.map(c => <option key={c.label} value={c.label}>{c.label}</option>)}
                                        </Select>
                                        {selectedCategory && <FormHelperText fontSize="2xs">Default: {WORK_ORDER_TYPES[selectedCategory.type]?.short} · {selectedCategory.priority}</FormHelperText>}
                                    </FormControl>
                                    {domainCfg?.linkField ? (
                                        <FormControl isRequired={domainCfg.linkRequired && !['emergency', 'installation', 'project'].includes(formData.work_order_type) && formData.domain !== 'road'}>
                                            <FormLabel fontSize="xs" color={formLabelColor} mb={1}>
                                                <HStack spacing={1}><Icon as={MdLink} /><Text>{domainCfg.linkLabel}</Text></HStack>
                                            </FormLabel>
                                            <Input name={domainCfg.linkField} size="sm" value={formData[domainCfg.linkField] || ''} onChange={handleInputChange}
                                                placeholder={`FlowSense ${domainCfg.linkLabel}`} aria-label={domainCfg.linkLabel} focusBorderColor="teal.500" />
                                            <FormHelperText fontSize="2xs">Links the job to the record's history and its AI models.</FormHelperText>
                                        </FormControl>
                                    ) : <Box />}
                                    {formData.domain === 'sensor' && (
                                        <FormControl>
                                            <FormLabel fontSize="xs" color={formLabelColor} mb={1}>Monitored asset ID (optional)</FormLabel>
                                            <Input name="asset_id" size="sm" value={formData.asset_id} onChange={handleInputChange} placeholder="Asset the sensor is on" aria-label="Monitored asset ID" focusBorderColor="teal.500" />
                                        </FormControl>
                                    )}
                                    {typeCfg?.fieldWork !== false && (
                                        <FormControl gridColumn={{ md: formData.domain === 'sensor' ? 'auto' : 'span 2' }}>
                                            <FormLabel fontSize="xs" color={formLabelColor} mb={1}>
                                                <HStack spacing={1}><Icon as={MdPlace} /><Text>Location</Text></HStack>
                                            </FormLabel>
                                            <Input name="location_description" size="sm" value={formData.location_description} onChange={handleInputChange}
                                                placeholder="Address / landmark, e.g. Bay St between East St and Victoria Ave" aria-label="Location" focusBorderColor="teal.500" />
                                            <Button size="xs" variant="link" colorScheme="teal" mt={1} onClick={() => setShowGps(v => !v)}>{showGps ? 'Hide GPS' : 'Add GPS coordinates'}</Button>
                                        </FormControl>
                                    )}
                                </SimpleGrid>
                                <Collapse in={showGps} animateOpacity>
                                    <SimpleGrid columns={2} spacing={3} mt={2}>
                                        <Input name="latitude" size="sm" type="number" step="any" value={formData.latitude} onChange={handleInputChange} placeholder="Latitude e.g. 25.0443" aria-label="Latitude" />
                                        <Input name="longitude" size="sm" type="number" step="any" value={formData.longitude} onChange={handleInputChange} placeholder="Longitude e.g. -77.3504" aria-label="Longitude" />
                                    </SimpleGrid>
                                </Collapse>
                                {(formData.parent_work_order_id || isInspection) && (
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} mt={3}>
                                        <FormControl>
                                            <FormLabel fontSize="xs" color={formLabelColor} mb={1}>
                                                <HStack spacing={1}><Icon as={MdFactCheck} /><Text>{isInspection ? 'Inspection of work order (optional)' : 'Raised from work order / inspection'}</Text></HStack>
                                            </FormLabel>
                                            <Input name="parent_work_order_id" size="sm" value={formData.parent_work_order_id} onChange={handleInputChange} placeholder="Parent work order ID" aria-label="Parent work order ID" />
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs" color={formLabelColor} mb={1}>Inspection source</FormLabel>
                                            <Select name="inspection_source" size="sm" value={formData.inspection_source} onChange={handleInputChange} placeholder="—" aria-label="Inspection source">
                                                {Object.entries(INSPECTION_SOURCES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                            </Select>
                                        </FormControl>
                                    </SimpleGrid>
                                )}
                            </Box>
                            {/* --- END UPDATE */}

                            {/* ---- Title ---- */}
                            <FormControl isRequired isInvalid={!!validationErrors.title}>
                                <FormLabel fontSize="sm" color={formLabelColor}>
                                    <HStack>
                                        <Icon as={MdTitle} />
                                        <Text>Title</Text>
                                        <Badge colorScheme="red">Required</Badge>
                                    </HStack>
                                </FormLabel>
                                <InputGroup>
                                    <Input
                                        name="title"
                                        placeholder="Brief, descriptive title of the task"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        focusBorderColor="teal.500"
                                    />
                                    <InputRightElement>
                                        <Text fontSize="xs" color={mutedTextColor}>{characterCounts.title}/200</Text>
                                    </InputRightElement>
                                </InputGroup>
                                {validationErrors.title ? (
                                    <FormHelperText color="red.500">{validationErrors.title}</FormHelperText>
                                ) : (
                                    <FormHelperText>Minimum 3 characters, maximum 200 characters</FormHelperText>
                                )}
                            </FormControl>

                            {/* ---- Description ---- */}
                            <FormControl isInvalid={!!validationErrors.description}>
                                <FormLabel fontSize="sm" color={formLabelColor}>
                                    <HStack>
                                        <Icon as={MdDescription} />
                                        <Text>Description</Text>
                                        <Badge colorScheme="gray">Optional</Badge>
                                        {isGeneratingDesc && (
                                            <HStack spacing={1} ml={1}>
                                                <Spinner size="xs" color="purple.400" />
                                                <Text fontSize="xs" color="purple.500" fontWeight="600">AI generating...</Text>
                                            </HStack>
                                        )}
                                    </HStack>
                                </FormLabel>
                                <Textarea
                                    name="description"
                                    placeholder="Detailed description of the work to be performed (optional)"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows={4}
                                    resize="vertical"
                                    focusBorderColor={isGeneratingDesc ? "purple.400" : "teal.500"}
                                    borderColor={isGeneratingDesc ? "purple.300" : undefined}
                                    isReadOnly={isGeneratingDesc}
                                    opacity={isGeneratingDesc ? 0.7 : 1}
                                    transition="all 0.2s"
                                />
                                <FormHelperText>
                                    {characterCounts.description}/2000 characters
                                    {isGeneratingDesc && (
                                        <Text as="span" color="purple.500" ml={2} fontWeight="600">
                                            • Ollama is writing the technical brief
                                        </Text>
                                    )}
                                </FormHelperText>
                            </FormControl>

                            {/* --- UPDATE: AI create assistant */}
                            <WorkOrderCreateGuide draft={formData} onApply={handleApplySuggestion} compact={!!prefillOrigin} />
                            {/* --- END UPDATE */}

                            <Divider />

                            {/* ---- Priority + SLA Preview Grid ---- */}
                            {/* --- UPDATE: Replaced Status/Priority 2-col grid with Priority + SLA Deadline Preview --- */}
                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                <GridItem>
                                    <FormControl>
                                        <FormLabel fontSize="sm" color={formLabelColor}>
                                            <HStack>
                                                <Icon as={MdPriorityHigh} />
                                                <Text>Priority</Text>
                                            </HStack>
                                        </FormLabel>
                                        <Select
                                            name="priority"
                                            value={formData.priority}
                                            onChange={handleInputChange}
                                            focusBorderColor="teal.500"
                                        >
                                            {ALLOWED_PRIORITY.map(p => (
                                                <option key={p} value={p}>
                                                    {p.toUpperCase()}
                                                </option>
                                            ))}
                                        </Select>
                                        <FormHelperText>
                                            <Badge colorScheme={getPriorityColor()} mr={1}>
                                                {formData.priority.toUpperCase()}
                                            </Badge>
                                            {PRIORITY_CONFIG[formData.priority]?.description}
                                        </FormHelperText>
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl>
                                        <FormLabel fontSize="sm" color={formLabelColor}>
                                            <HStack>
                                                <Icon as={MdTimer} color="orange.500" />
                                                <Text>Strict SLA Deadline Preview</Text>
                                            </HStack>
                                        </FormLabel>
                                        <Box
                                            p={2}
                                            borderWidth="1px"
                                            borderRadius="md"
                                            bg={slaBg}
                                            borderColor="orange.200"
                                        >
                                            <Text fontSize="sm" fontWeight="bold" color="orange.600">
                                                {slaPreview}
                                            </Text>
                                            <Text fontSize="xs" color="orange.500">
                                                Backend will enforce a {PRIORITY_CONFIG[formData.priority]?.slaTarget}h window.
                                            </Text>
                                        </Box>
                                    </FormControl>
                                </GridItem>
                            </Grid>
                            {/* --- END UPDATE --- */}

                            {/* ---- Status (preserved from Doc 2, shown below the priority grid) ---- */}
                            <FormControl>
                                <FormLabel fontSize="sm" color={formLabelColor}>
                                    <HStack>
                                        <Icon as={MdAssignment} />
                                        <Text>Status</Text>
                                    </HStack>
                                </FormLabel>
                                <Select
                                    name="status"
                                    value={formData.status}
                                    onChange={handleInputChange}
                                    focusBorderColor="teal.500"
                                >
                                    {ALLOWED_STATUS.map(s => (
                                        <option key={s} value={s}>
                                            {s.charAt(0).toUpperCase() + s.slice(1).replace('_', ' ')}
                                        </option>
                                    ))}
                                </Select>
                                <FormHelperText>Initial status (usually pending)</FormHelperText>
                            </FormControl>

                            {/* ---- Due Date + Assigned User Grid ---- */}
                            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                                <GridItem>
                                    <FormControl isInvalid={!!validationErrors.due_date}>
                                        <FormLabel fontSize="sm" color={formLabelColor}>
                                            <HStack>
                                                <Icon as={MdCalendarToday} />
                                                <Text>Due Date</Text>
                                                <Badge colorScheme="gray">Optional</Badge>
                                            </HStack>
                                        </FormLabel>
                                        <Input
                                            type="date"
                                            name="due_date"
                                            value={formData.due_date}
                                            onChange={handleInputChange}
                                            focusBorderColor="teal.500"
                                        />
                                        {validationErrors.due_date ? (
                                            <FormHelperText color="red.500">{validationErrors.due_date}</FormHelperText>
                                        ) : (
                                            <FormHelperText>Target completion date (defaults to 7 days from now)</FormHelperText>
                                        )}
                                    </FormControl>
                                </GridItem>

                                <GridItem>
                                    <FormControl>
                                        <FormLabel fontSize="sm" color={formLabelColor}>
                                            <HStack>
                                                <Icon as={MdPerson} />
                                                <Text>Assigned User ID</Text>
                                                <Badge colorScheme="gray">Optional</Badge>
                                            </HStack>
                                        </FormLabel>
                                        <Input
                                            name="assigned_user_id"
                                            placeholder="Leave empty to assign later"
                                            value={formData.assigned_user_id}
                                            onChange={handleInputChange}
                                            focusBorderColor="teal.500"
                                        />
                                        <FormHelperText>You can assign this work order to a user later</FormHelperText>
                                    </FormControl>
                                </GridItem>
                            </Grid>

                            {/* --- UPDATE: linked QA inspection after the job */}
                            {!isInspection && typeCfg?.fieldWork && (
                                <Checkbox isChecked={scheduleQa} onChange={(e) => setScheduleQa(e.target.checked)} colorScheme="teal" size="sm">
                                    Schedule a QA inspection of this job (linked inspection work order, due {formData.due_date || `in ${QA_INSPECTION_DAYS} days`})
                                </Checkbox>
                            )}
                            {/* --- END UPDATE */}

                            {/* ---- Work Order Preview Card ---- */}
                            {formData.title && (
                                <Fade in={true}>
                                    <Box
                                        p={4}
                                        bg={cardBg}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={borderColor}
                                    >
                                        <HStack mb={2}>
                                            <Icon as={MdCheckCircle} color="green.500" />
                                            <Text fontSize="sm" fontWeight="bold">Work Order Preview</Text>
                                        </HStack>
                                        <VStack align="start" spacing={1} fontSize="xs">
                                            <Text><strong>Title:</strong> {formData.title}</Text>
                                            <HStack spacing={2}>
                                                <Badge colorScheme={getPriorityColor()}>
                                                    {formData.priority.toUpperCase()}
                                                </Badge>
                                                <Badge colorScheme="blue">
                                                    {formData.status.toUpperCase()}
                                                </Badge>
                                                {/* --- UPDATE */}
                                                {typeCfg && <Badge colorScheme={typeCfg.color}>{typeCfg.short}</Badge>}
                                                {domainCfg && <Badge colorScheme={domainCfg.color} variant="outline">{domainCfg.short}</Badge>}
                                                {/* --- END UPDATE */}
                                            </HStack>
                                            {formData.due_date && (
                                                <Text>
                                                    <strong>Due:</strong> {new Date(`${formData.due_date}T00:00:00`).toLocaleDateString()}{/* --- UPDATE: no UTC day shift */}
                                                </Text>
                                            )}
                                            {/* --- UPDATE: Show SLA deadline in preview card --- */}
                                            <Text>
                                                <strong>SLA Deadline:</strong>{' '}
                                                <Text as="span" color="orange.500" fontWeight="600">{slaPreview}</Text>
                                            </Text>
                                            {/* --- END UPDATE --- */}
                                            {/* --- UPDATE */}
                                            {formData.category && <Text><strong>Category:</strong> {formData.category}</Text>}
                                            {domainCfg?.linkField && formData[domainCfg.linkField] && <Text><strong>Linked {domainCfg.linkLabel}:</strong> {formData[domainCfg.linkField]}</Text>}
                                            {formData.parent_work_order_id && <Text><strong>Parent work order:</strong> {formData.parent_work_order_id}</Text>}
                                            {/* --- END UPDATE */}
                                        </VStack>
                                    </Box>
                                </Fade>
                            )}

                            {/* ---- Debug Panel ---- */}
                            {DEBUG_CONFIG.enabled && showDebugPanel && (
                                <Collapse in={showDebugPanel} animateOpacity>
                                    <Box
                                        p={3}
                                        bg={debugPanelBg}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={debugBorderColor}
                                        fontSize="xs"
                                        fontFamily="mono"
                                    >
                                        <HStack justify="space-between" mb={2}>
                                            <HStack>
                                                <Icon as={MdBugReport} />
                                                <Text fontWeight="bold">Debug Panel</Text>
                                            </HStack>
                                            <IconButton
                                                icon={<MdClose />}
                                                size="xs"
                                                onClick={() => setShowDebugPanel(false)}
                                                aria-label="Close debug"
                                            />
                                        </HStack>
                                        <VStack align="stretch" spacing={1}>
                                            <Text><strong>Form Progress:</strong> {formProgress}%</Text>
                                            <Text><strong>Title Length:</strong> {characterCounts.title}/200</Text>
                                            <Text><strong>Description Length:</strong> {characterCounts.description}/2000</Text>
                                            <Text><strong>Validation Errors:</strong> {Object.keys(validationErrors).length}</Text>
                                            <Text><strong>Current User:</strong> {currentUser?.username || 'N/A'}</Text>
                                            <Text><strong>User ID:</strong> {currentUser?.id || 'N/A'}</Text>
                                            <Text><strong>Submitting:</strong> {isSubmitting ? 'Yes' : 'No'}</Text>
                                            <Text><strong>AI Triage Mode:</strong> {isAITriageMode ? 'Yes' : 'No'}</Text>
                                            <Text><strong>AI Generating Desc:</strong> {isGeneratingDesc ? 'Yes' : 'No'}</Text>
                                            {/* --- UPDATE: SLA preview in debug panel --- */}
                                            <Text><strong>SLA Preview:</strong> {slaPreview}</Text>
                                            <Text><strong>SLA Window:</strong> {PRIORITY_CONFIG[formData.priority]?.slaTarget}h</Text>
                                            {/* --- END UPDATE --- */}
                                        </VStack>
                                    </Box>
                                </Collapse>
                            )}
                        </VStack>
                    </ModalBody>

                    {/* ==================== FOOTER ==================== */}
                    <ModalFooter>
                        {/* --- UPDATE: Button label changed to "Dispatch Work Order" --- */}
                        <Button
                            type="submit"
                            colorScheme="teal"
                            mr={3}
                            isLoading={isSubmitting}
                            loadingText="Dispatching..."
                            leftIcon={<MdAdd />}
                            isDisabled={!!validationErrors.title || !formData.title || isGeneratingDesc}
                            title={readiness.ready ? 'Everything the AI needs is filled in' : `Still recommended: ${readiness.missingRequired.map(s => s.label).join(', ')}`}
                        >
                            {isInspection ? 'Create Inspection' : 'Dispatch Work Order'}{/* --- UPDATE */}
                        </Button>
                        {/* --- END UPDATE --- */}
                        <Button variant="ghost" onClick={onClose} isDisabled={isSubmitting}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </ScaleFade>
        </Modal>
    );
}

CreateWorkOrderForm.propTypes = {
    isOpen:    PropTypes.bool.isRequired,
    onClose:   PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    // --- UPDATE: draft from workOrderCatalog.buildDraft(...) — asset, sensor, well, road, inspection …
    prefill:   PropTypes.object,
    // --- END UPDATE
};

export default CreateWorkOrderForm;